import {
  NotificationChannelLabel,
  NotificationEventDescription,
  NotificationEventLabel
} from '@core/models/notification.enum';
import { TcGateState } from '@core/models/tc.models';

import {
  ConsignorStatus,
  DispositionPreference,
  DispositionPreferenceLabel,
  PaymentMethod,
  PaymentMethodLabel,
  Tax1099Status,
  Tax1099StatusLabel,
  TaxEntityType,
  TaxEntityTypeLabel
} from './profile.enum';
import { ConsignorProfileHttpResponse, ConsignorProfileResponse } from './profile.response';
import { ConsignorAddressState, ConsignorProfileState, NotificationPreferenceState } from './profile.state';

/** Renders a structured address as a single comma-separated line for compact display. */
export const formatMailingAddress = (address: ConsignorAddressState | undefined): string =>
  address
    ? [address.line1, address.line2, `${address.city} ${address.stateName} ${address.zipCode}`.trim()]
        .filter(Boolean)
        .join(', ')
    : '';

export const mapConsignorProfileResponseToState = (res: ConsignorProfileResponse): ConsignorProfileState => ({
  id: res.id,
  fullName: res.fullName,
  userName: res.userName,
  consignorStatus: ConsignorStatus.Active,
  businessName: res.businessName,
  email: res.email,
  phoneNumber: res.phoneNumber,
  mailingAddress: res.mailingAddress,
  memberSince: res.memberSince,
  storeLocationName: res.storeLocationName,
  dispositionPreference: res.dispositionPreference,
  dispositionPreferenceLabel: DispositionPreferenceLabel[res.dispositionPreference],
  payment: {
    method: res.payment.method,
    methodLabel: PaymentMethodLabel[res.payment.method],
    maskedBankDetails: res.payment.maskedBankDetails,
    maskedCheckAddress: res.payment.maskedCheckAddress,
    storeCreditBalance: res.payment.storeCreditBalance
  },
  tax: {
    status: res.tax.status,
    statusLabel: Tax1099StatusLabel[res.tax.status],
    taxEntityTypeLabel: res.tax.taxEntityTypeLabel,
    legalName: res.tax.legalName,
    taxIdLast4: res.tax.taxIdLast4,
    mailingAddress: res.tax.mailingAddress,
    isComplete: res.tax.isComplete,
    ytdPayoutTotal: res.tax.ytdPayoutTotal,
    threshold: res.tax.threshold
  },
  taxDocuments: res.taxDocuments.map(d => ({
    year: d.year,
    formType: d.formType,
    generatedAt: d.generatedAt,
    downloadUrl: d.downloadUrl
  })),
  notifications: res.notifications.map(mapNotification),
  tc: res.tc,
  accountSuspended: res.accountSuspended
});

const mapNotification = (n: {
  event: import('@core/models/notification.enum').NotificationEvent;
  channel: import('@core/models/notification.enum').NotificationChannel;
  enabled: boolean;
  configurable: boolean;
}): NotificationPreferenceState => ({
  event: n.event,
  eventLabel: NotificationEventLabel[n.event],
  eventDescription: NotificationEventDescription[n.event],
  channel: n.channel,
  channelLabel: NotificationChannelLabel[n.channel],
  enabled: n.enabled,
  configurable: n.configurable
});

export const mapConsignorProfileHttpResponseToState = (
  res: ConsignorProfileHttpResponse,
  tc: TcGateState
): ConsignorProfileState => {
  const tax1099Status = res.tax1099Status as Tax1099Status;
  const paymentMethod = res.paymentMethod as PaymentMethod;
  const dispositionPref = res.dispositionPreference as DispositionPreference;
  const address = res.address
    ? {
        line1: res.address.line1,
        line2: res.address.line2 ?? undefined,
        city: res.address.city,
        stateName: res.address.stateName,
        countryName: res.address.countryName,
        zipCode: res.address.zipCode
      }
    : undefined;

  return {
    id: String(res.id),
    fullName: `${res.firstName} ${res.lastName}`.trim(),
    userName: res.userName,
    consignorStatus: res.status as ConsignorStatus,
    businessName: res.businessName ?? undefined,
    email: res.email,
    phoneNumber: res.phoneNumber,
    mailingAddress: formatMailingAddress(address),
    address,
    memberSince: res.memberSince,
    storeLocationName: res.preferredStore?.name ?? '',
    dispositionPreference: dispositionPref,
    dispositionPreferenceLabel: DispositionPreferenceLabel[dispositionPref],
    payment: {
      method: paymentMethod,
      methodLabel: PaymentMethodLabel[paymentMethod],
      maskedBankDetails: res.paymentAccountLast4 ? `****${res.paymentAccountLast4}` : undefined
    },
    tax: {
      status: tax1099Status,
      statusLabel: Tax1099StatusLabel[tax1099Status],
      taxEntityTypeLabel: res.taxEntityType ? TaxEntityTypeLabel[res.taxEntityType as TaxEntityType] : undefined,
      isComplete: tax1099Status >= Tax1099Status.TaxInfoComplete,
      ytdPayoutTotal: 0,
      threshold: 600
    },
    taxDocuments: [],
    notifications: [],
    tc,
    accountSuspended: tc.suspended
  };
};
